/**
 * Page Metadata Configuration
 *
 * This file contains metadata templates for different page types in the application.
 * Each section is clearly marked for easy configuration and updates.
 */

// ============================================================================
// STANDARD TRIVIA PAGES (TagLandingPage)
// ============================================================================
// These are category-based trivia pages like /science, /history, etc.
// Template format:
// - Title: "[Category Name] Trivia | Play Unlimited Quizzes Free"
// - Description: "Play [category name] trivia on Atrivial. Test how far you can make it in endless [category name] questions."

export interface TagTriviaMetadata {
  title: string;
  description: string;
}

export function getNotFoundPageMetadata() {
  return [{ title: 'Page not found | Atrivial' }, { name: 'robots', content: 'noindex' }];
}

/**
 * Capitalizes the first letter of a string
 */
function capitalizeFirst(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getTagTriviaMetadata(tagName: string): TagTriviaMetadata {
  // Capitalize first letter for display (handles slugs like "mathematics" -> "Mathematics")
  const capitalizedName = capitalizeFirst(tagName);
  const lowerName = tagName.toLowerCase();

  return {
    title: `${capitalizedName} Trivia | Play Unlimited Quizzes Free`,
    description: `Play ${lowerName} trivia on Atrivial. Test how far you can make it in endless ${lowerName} questions.`,
  };
}

// ============================================================================
// QUIZ PAGES (QuizPage)
// ============================================================================
// These are individual quiz pages like /quiz/some-slug
// Template format:
// - Title: "Atrivial: [Quiz Title]"
// - Description: "Play the [quiz title] quiz on Atrivial"

// ============================================================================
// PARTNER PAGES (PartnerTagLandingPage, PartnerTriviaHistory, etc.)
// ============================================================================
// These are partner-branded trivia pages
// Template formats:
// - Partner Tag Landing: "[Category Name]" and "Play trivia about [category name]"
// - Publisher Trivia (with tag): "[Publisher Name] Trivia - [Category Name]" and "Play [category name] trivia curated by [Publisher Name]"
// - Publisher Trivia (general): "[Publisher Name] Trivia" and "Play trivia curated by [Publisher Name]"
// - Publisher Landing: "A Trivial Experience, powered by [Publisher Name]: [Category Name] Trivia" and "Play trivia questions about [Category Name]"

export interface PartnerTagLandingMetadata {
  title: string;
  description: string;
}

export interface PartnerTriviaMetadata {
  title: string;
  description: string;
}

export interface PartnerLandingMetadata {
  title: string;
  description: string;
}

/**
 * Metadata for partner tag landing pages (e.g., /partner/history/science)
 */
export function getPartnerTagLandingMetadata(categoryName: string): PartnerTagLandingMetadata {
  return {
    title: categoryName,
    description: `Play trivia about ${categoryName.toLowerCase()}`,
  };
}

/**
 * Metadata for partner trivia pages (e.g., /partner/history/play-trivia)
 * @param partnerName - The partner name (e.g., "HISTORY")
 * @param categoryName - Optional category name if filtering by tag
 */
export function getPartnerTriviaMetadata(
  partnerName: string,
  categoryName?: string
): PartnerTriviaMetadata {
  if (categoryName) {
    return {
      title: `${partnerName} Trivia - ${categoryName}`,
      description: `Play ${categoryName.toLowerCase()} trivia curated by ${partnerName}.`,
    };
  }
  return {
    title: `${partnerName} Trivia`,
    description: `Play trivia curated by ${partnerName}.`,
  };
}

/**
 * Metadata for partner landing pages (e.g., /partner/history)
 * @param partnerName - The partner name (e.g., "HISTORY")
 * @param categoryName - The category name (e.g., "History")
 */
export function getPartnerLandingMetadata(
  partnerName: string,
  categoryName: string
): PartnerLandingMetadata {
  return {
    title: `A Trivial Experience, powered by ${partnerName}: ${categoryName} Trivia`,
    description: `Play trivia questions about ${categoryName}.`,
  };
}

/** Metadata for the WRAL trivia page (/partner/wral). */
export function getWralTriviaMetadata(): PartnerTriviaMetadata {
  return {
    title: 'WRAL Trivia | Atrivial',
    description: 'Play WRAL Trivia, powered by Atrivial.',
  };
}

// ============================================================================
// CUSTOM PAGES (EndlessTriviaStateMachine, DailyChallengeStateMachine)
// ============================================================================
// These are unique custom pages that don't follow standard templates
// Each has its own specific metadata configuration

export interface CustomPageMetadata {
  title: string;
  description: string;
}

/**
 * Metadata for the Endless Trivia page (/endless-trivia)
 * Template format:
 * - Title: "Atrivial: Endless Trivia"
 * - Description: "Play endless trivia game."
 */
export function getEndlessTriviaMetadata(): CustomPageMetadata {
  return {
    title: 'Atrivial: Endless Trivia',
    description: 'Play endless trivia game.',
  };
}

/**
 * Metadata for the Daily Challenge page (/daily)
 * Template format:
 * - Title: "Atrivial: Daily Challenge"
 * - Description: "Play today's trivia challenge on Atrivial."
 */
export function getDailyChallengeMetadata(): CustomPageMetadata {
  return {
    title: 'Atrivial: Daily Challenge',
    description: "Play today's trivia challenge on Atrivial.",
  };
}

// ============================================================================
// STRUCTURED DATA (JSON-LD) SCHEMAS
// ============================================================================

/**
 * Organization schema for all pages
 * Returns JSON-LD structured data for the Atrivial organization
 */
export function getOrganizationSchema(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: 'https://atrivial.com/',
    name: 'Atrivial',
    description:
      'Atrivial helps publishers turn newsletter and web content into interactive Quizzes with Newsletter HTML embeds, hosted Quiz links, and partner analytics.',
    logo: 'https://atrivial.com/logo.svg',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      url: 'https://atrivial.com/contact-us',
    },
  };
}

/**
 * Options for generating game schema
 */
export interface GameSchemaOptions {
  name: string;
  url: string;
  description: string;
  image?: string;
}

/**
 * WebApplication/VideoGame schema for trivia game pages
 * Returns JSON-LD structured data for trivia games
 */
export function getGameSchema(options: GameSchemaOptions): object {
  const { name, url, description, image = 'https://atrivial.com/logo.svg' } = options;

  return {
    '@context': 'https://schema.org',
    '@type': ['VideoGame', 'WebApplication'],
    name,
    url,
    description,
    applicationSubCategory: 'Trivia',
    operatingSystem: ['Windows', 'Chrome OS', 'Linux', 'MacOS', 'Android', 'iOS'],
    applicationCategory: 'Game',
    isAccessibleForFree: true,
    image: {
      '@type': 'ImageObject',
      url: image,
    },
  };
}

/**
 * Represents a single question in the Quiz schema
 */
export interface QuizSchemaQuestion {
  questionText: string;
  acceptedAnswerText: string;
}

/**
 * Options for generating quiz schema
 */
export interface QuizSchemaOptions {
  name: string;
  url: string;
  description: string;
  image?: string;
  questions?: QuizSchemaQuestion[];
}

/**
 * Quiz schema for individual quiz pages
 * Returns JSON-LD structured data for quizzes (tests of knowledge)
 * Reference: https://schema.org/Quiz
 */
export function getQuizSchema(options: QuizSchemaOptions): object {
  const { name, url, description, image = 'https://atrivial.com/logo.svg', questions } = options;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Quiz',
    name,
    url,
    description,
    isAccessibleForFree: true,
    image: {
      '@type': 'ImageObject',
      url: image,
    },
  };

  if (questions && questions.length > 0) {
    schema.hasPart = questions.map((q) => ({
      '@type': 'Question',
      name: q.questionText,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.acceptedAnswerText,
      },
    }));
  }

  return schema;
}

/**
 * Item for ItemList schema
 */
export interface ItemListItem {
  name: string;
  url: string;
}

/**
 * ItemList schema for lists of trivia/quiz links
 * Returns JSON-LD structured data for lists of related pages
 * Reference: https://schema.org/ItemList
 */
export function getItemListSchema(items: ItemListItem[]): object {
  if (items.length === 0) {
    return {};
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'WebPage',
        name: item.name,
        url: item.url,
      },
    })),
  };
}
